import { MenuItem } from '@cloudclevr/components';
import {
  useAddDashboardTile,
  useDashboardUpdateTileParams,
  useRemoveDashboardTile,
} from '@hooks/dashboard';
import { useEffect, useState } from 'react';

import {
  LineChartTile,
  LineChartTypes,
} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';
import { getTrendData, getTrendLabels } from '@utils/index';
import { useMeetingRoomsQuery, useMeetingRoomsTrend } from '@hooks/adoption';
import { TilesProps } from '@common/types';
import { DashboardTiles } from '@common/constants';

type Params = {
  roomId: string;
  days: string;
};

export const MeetingRoomsTrend: React.FC<TilesProps> = ({
  params,
  id,
  classNames,
}) => {
  const [parameters, setParameters] = useState<Params>(JSON.parse(params));

  const useDashboardUpdateTileParamsMutation = useDashboardUpdateTileParams();
  const { removeMenuItem } = useRemoveDashboardTile(id);

  const { data, isLoading, refetch } = useMeetingRoomsTrend(parameters);

  const { data: meetingRoomsData } = useMeetingRoomsQuery();

  const isOnDashboardPage = !!id;

  const compareAgainstTrendData = getTrendData(
    data?.compareAgainstTrend || [],
    +parameters.days
  );

  const labels = getTrendLabels(data?.trend || [], +parameters.days);

  const trendData = getTrendData(data?.trend || [], +parameters.days);

  useEffect(() => {
    const updatedParams = JSON.parse(params);

    setParameters({ days: updatedParams.days, roomId: parameters.roomId });

    return () => {
      refetch();
    };
  }, [parameters.roomId, params, refetch]);

  const handleSortDropdownChange = (value: string) => {
    const newParameters = {
      ...parameters,
      roomId: value,
    };

    setParameters(newParameters);

    if (isOnDashboardPage) {
      useDashboardUpdateTileParamsMutation.mutate({
        params: JSON.stringify(newParameters),
        tileId: id,
      });

      refetch();
    }
  };

  const { addMenuItem } = useAddDashboardTile(
    DashboardTiles.MeetingRoomsTrend,
    parameters
  );

  const sortMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'All rooms',
      value: 'allrooms',
      action: () => handleSortDropdownChange('allrooms'),
    },
  ];

  if (meetingRoomsData) {
    meetingRoomsData.rooms.forEach((room, index) => {
      sortMenuItems.push({
        id: index + 2,
        label: room.name,
        value: room.id,
        action: () => handleSortDropdownChange(room.id),
      });
    });
  }

  return (
    parameters && (
      <LineChartTile
        contextData={isOnDashboardPage ? removeMenuItem : addMenuItem}
        sortData={sortMenuItems}
        initialSelectedItem={sortMenuItems.find(
          (item) => item.value === parameters.roomId
        )}
        compareData={undefined}
        dropdownMenuItemClasses={'!w-[300px]'}
        classNames={
          classNames ? classNames : 'col-span-6 md:col-span-6 xl:col-span-4'
        }
        tooltipLabel={['', '']}
        legendLabels={[`Last ${parameters.days} days`, 'Previous period']}
        headerTitle={'Total hours booked / day'}
        dataSetTypes={[LineChartTypes.Dots, LineChartTypes.Line]}
        compareLabel=""
        buttonLabel=""
        dataSetColors={['#00CF6C', '#3F454B']}
        isLoading={isLoading}
        labels={labels}
        data={[trendData, compareAgainstTrendData]}
        dropdownSortMenuItemClasses={'!w-[240px]'}
        adjustedPosition={{ left: '0' }}
      />
    )
  );
};
